import { ApiError, api } from "@/lib/apiClient";

/* ─────────────────────────── Typen ─────────────────────────── */

export interface OrderPosition {
  typ: "hero" | "freitext";
  menge: number;
  einheit?: string | null;
  text: string;
  hero_nr?: string | null;
  hero_ean?: string | null;
  hero_unit?: string | null;
  auftrag_id: number;
  lot_nummer?: string | null;
  angebot_preis?: number | null;
  angebot_waehrung?: string | null;
  angebot_gueltig_bis?: string | null;
  angebot_datei_url?: string | null;
  bestellnummer?: string | null;
  auftragsnummer?: string | null;
  source_anfrage_position_id?: number | null;
  source_ma_position_id?: number | null;
}

export interface AuftragMini {
  id: number;
  jahr: number;
  ordnernummer?: string | null;
  nummer?: number | null;
  auftragsnummer?: string | null;
  auftragsnummer_display?: string | null;
  ort?: string | null;
  beschreibung?: string | null;
}

export interface OrderDraftIn {
  supplier_id: string | number | null;
  supplier_name?: string | null;
  supplier_contact_first_name?: string | null;
  supplier_contact_last_name?: string | null;
  supplier_contact_anrede?: string | null;
  supplier_contact_name?: string | null;
  supplier_email?: string | null;
  template_id?: number | null;
  kuerzel: string;
  auftraege: AuftragMini[];
  positionen: OrderPosition[];
  notizen?: string | null;
  source_ma_id?: number | null;
  source_anfrage_id?: number | null;
  lieferadresse?: string | null;
  lieferadresse_id?: number | null;
  lieferadresse_zhd?: string | null;
  lieferadresse_firma?: string | null;
  lieferadresse_strasse?: string | null;
  lieferadresse_hausnummer?: string | null;
  lieferadresse_postleitzahl?: string | null;
  lieferadresse_stadt?: string | null;
  lieferart?: "lieferung" | "abholung" | null;
}

export interface OrderDraft extends OrderDraftIn {
  id: number;
  status: "draft" | "ready" | "sent";
  created_by: string;
  created_at: string;
  updated_at: string;
  supplier_gp_id?: number | null;
  supplier_has_logo?: boolean | null;
  supplier_logo_updated_at?: string | null;
}

export interface EmailTemplate {
  id: number;
  name: string;
  subject: string;
  body: string;
  is_default: boolean;
}

export interface DeliveryAddress {
  id: number;
  abkuerzung?: string | null;
  firma: string;
  strasse?: string | null;
  hausnummer?: string | null;
  postleitzahl?: string | null;
  stadt?: string | null;
  land?: string | null;
  google_place_id?: string | null;
  notizen?: string | null;
  is_default?: boolean;
  usage_count?: number;
}

export interface OrderHistoryProject {
  auftrag_id: number;
  auftragsnummer?: string | null;
  ort?: string | null;
  beschreibung?: string | null;
  ordnerpfad?: string | null;
  sharepoint_folder_id?: string | null;
}

export interface OrderHistoryLine {
  id?: number | null;
  menge?: number | null;
  lot_nummer?: string | null;
  einheit?: string | null;
  text?: string | null;
  hero_nr?: string | null;
  hero_ean?: string | null;
  hero_unit?: string | null;
  angebot_preis?: number | null;
  angebot_waehrung?: string | null;
  angebot_gueltig_bis?: string | null;
  angebot_datei_url?: string | null;
  auftrag_id?: number | null;
  bestellnummer?: string | null;
  auftragsnummer?: string | null;
}

export interface OrderAnfrageLink {
  anfrage_id: number;
  anfrage_subject?: string | null;
  ta_nummern?: string[];
  created_at?: string;
  created_by?: string;
  position_ids?: number[];
}

export interface OrderHistoryRow {
  id: number;
  created_at: string;
  supplier_id?: string | number | null;
  supplier_name?: string | null;
  supplier_email?: string | null;
  template_id?: number | null;
  subject?: string | null;
  kuerzel?: string | null;
  supplier_contact_salutation?: string | null;
  supplier_contact_first_name?: string | null;
  supplier_contact_last_name?: string | null;
  supplier_contact_name?: string | null;
  supplier_gp_id?: number | null;
  supplier_has_logo?: boolean | null;
  supplier_logo_updated_at?: string | null;
  bestellnummern?: string | null;
  submission_channel?: string | null;
  lieferart?: string | null;
  lieferadresse_abkuerzung?: string | null;
  positionen?: OrderHistoryLine[];
  projects?: OrderHistoryProject[];
  source_ma?: { id: number; ma_nummer?: string | null } | null;
  anfrage_links?: OrderAnfrageLink[];
  change_log_count?: number;
  cancelled_at?: string | null;
  geliefert_at?: string | null;
  werkszeugnis_count?: number | null;
}

export interface OrderWerkszeugnisLink {
  kind: "infohub" | "project" | string;
  label: string;
  web_url?: string | null;
  folder_path?: string | null;
}

export interface OrderWerkszeugnis {
  id: number;
  order_id: number;
  filename: string;
  content_type?: string | null;
  size_bytes: number;
  uploaded_by?: string | null;
  uploaded_at?: string | null;
  order_line_id?: number | null;
  lot_nummer?: string | null;
  links: OrderWerkszeugnisLink[];
}

export interface OrderFilterOptions {
  suppliers: string[];
  bestellers: string[];
  contacts: string[];
}

export interface FinalizeResult {
  subject: string;
  body: string;
  order_ids: Record<string, number>;
  bestellnummern: Record<string, string>;
}

export interface ChangeLogEntry {
  action?: string;
  reason?: string | null;
  changed_at: string;
  changed_by: string;
  before_snapshot?: {
    lines?: OrderHistoryLine[];
    geliefert_at?: string | null;
    lot_nummer?: string | null;
    text?: string | null;
  };
  after_snapshot?: {
    lines?: OrderHistoryLine[];
    geliefert_at?: string | null;
    lot_nummer?: string | null;
    text?: string | null;
  };
}

export interface HeroSupplier {
  id: string | number;
  name: string;
  email?: string | null;
  geschaeftspartner_id?: number | null;
  hero_contact_id?: number | null;
  order_count?: number;
  has_logo?: boolean | null;
  logo_updated_at?: string | null;
}

export interface HeroSupplierContact {
  label: string;
  value: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  title?: string;
  /** Allgemeine Mail des Kontakts, nicht eine Person. */
  is_company_email?: boolean;
}

export interface TopProductRow {
  hero_nr?: string | null;
  text?: string | null;
  einheit?: string | null;
  hero_unit?: string | null;
  hero_ean?: string | null;
  order_count?: number;
  image_src?: string | null;
}

export interface SharePointMailItem {
  id: string;
  name: string;
  size?: number;
  project_hint?: string;
}

export interface ListOrdersParams {
  id?: number;
  q?: string;
  dateFrom?: string;
  dateTo?: string;
  supplierName?: string;
  createdBy?: string;
  contactName?: string;
  typo?: boolean;
  tokensAll?: boolean;
  openOnly?: boolean;
  limit?: number;
  offset?: number;
}

function buildOrdersQuery(params: ListOrdersParams): string {
  const usp = new URLSearchParams();
  if (params.id != null && params.id > 0) usp.set("id", String(params.id));
  if (params.q) usp.set("q", params.q);
  if (params.dateFrom) usp.set("date_from", params.dateFrom);
  if (params.dateTo) usp.set("date_to", params.dateTo);
  if (params.supplierName) usp.set("supplier_name", params.supplierName);
  if (params.createdBy) usp.set("created_by", params.createdBy);
  if (params.contactName) usp.set("contact_name", params.contactName);
  if (params.openOnly) usp.set("open_only", "1");
  if (params.limit != null) usp.set("limit", String(params.limit));
  if (params.offset != null && params.offset > 0) usp.set("offset", String(params.offset));
  if (params.typo !== undefined) usp.set("typo", params.typo ? "1" : "0");
  if (params.tokensAll !== undefined) usp.set("tokens_all", params.tokensAll ? "1" : "0");
  usp.set("normalize", "1");
  usp.set("prefix", "1");
  return usp.toString();
}

export async function listOrders(params: ListOrdersParams = {}): Promise<OrderHistoryRow[]> {
  const qs = buildOrdersQuery({ ...params, typo: params.typo ?? true, tokensAll: params.tokensAll ?? false });
  return api.get<OrderHistoryRow[]>(`/orders${qs ? `?${qs}` : ""}`);
}

export async function getOrderById(orderId: number): Promise<OrderHistoryRow | null> {
  if (!Number.isFinite(orderId) || orderId <= 0) return null;
  const rows = await listOrders({ id: orderId });
  return rows[0] ?? null;
}

export async function listOrdersWithFallback(
  params: ListOrdersParams,
): Promise<{ rows: OrderHistoryRow[]; altMode: boolean }> {
  let rows = await listOrders(params);
  let altMode = false;
  if (!rows.length && params.q?.trim()) {
    altMode = true;
    rows = await listOrders({ ...params, typo: true, tokensAll: false });
  }
  return { rows, altMode };
}

function splitBestellnummern(raw: string | null | undefined): string[] {
  return String(raw || "")
    .split(/[,;]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

/** Bestellhistorie anhand einer TK-/Bestellnummer (Notiz-Referenzen). */
export async function findOrderHistoryByBestellnummer(
  bestellnummer: string,
): Promise<OrderHistoryRow | null> {
  const needle = String(bestellnummer || "").trim();
  if (!needle) return null;
  const { rows } = await listOrdersWithFallback({ q: needle });
  return (
    rows.find((row) => {
      if (splitBestellnummern(row.bestellnummern).includes(needle)) return true;
      return (row.positionen || []).some(
        (line) => String(line.bestellnummer || "").trim() === needle,
      );
    }) ?? null
  );
}

export function getOrderFilterOptions(params: {
  dateFrom?: string;
  dateTo?: string;
  supplierName?: string;
  createdBy?: string;
  contactName?: string;
  openOnly?: boolean;
}): Promise<OrderFilterOptions> {
  const usp = new URLSearchParams();
  if (params.dateFrom) usp.set("date_from", params.dateFrom);
  if (params.dateTo) usp.set("date_to", params.dateTo);
  if (params.supplierName) usp.set("supplier_name", params.supplierName);
  if (params.createdBy) usp.set("created_by", params.createdBy);
  if (params.contactName) usp.set("contact_name", params.contactName);
  if (params.openOnly) usp.set("open_only", "1");
  const qs = usp.toString();
  return api.get<OrderFilterOptions>(`/orders/filter-options${qs ? `?${qs}` : ""}`);
}

export function listOrderDrafts(onlyMine = true): Promise<OrderDraft[]> {
  const qs = onlyMine ? "?only_mine=true" : "";
  return api.get<OrderDraft[]>(`/orders/drafts${qs}`);
}

export function createOrderDraft(payload: OrderDraftIn): Promise<OrderDraft> {
  return api.post<OrderDraft>("/orders/drafts", payload);
}

export function updateOrderDraft(draftId: number, payload: OrderDraftIn): Promise<OrderDraft> {
  return api.patch<OrderDraft>(`/orders/drafts/${draftId}`, payload);
}

export function deleteOrderDraft(draftId: number): Promise<void> {
  return api.del(`/orders/drafts/${draftId}`);
}

export function listEmailTemplates(): Promise<EmailTemplate[]> {
  return api.get<EmailTemplate[]>("/orders/email-templates");
}

export function getTemplatePreference(
  supplierId: string,
  contactKey: string,
): Promise<{ template_id: number } | null> {
  const usp = new URLSearchParams({ supplier_id: supplierId, contact_key: contactKey });
  return api.get(`/orders/template-preference?${usp}`);
}

export function saveTemplatePreference(
  supplierId: string,
  contactKey: string,
  templateId: number,
): Promise<unknown> {
  return api.post("/orders/template-preference", {
    supplier_id: supplierId,
    contact_key: contactKey,
    template_id: templateId,
  });
}

export function listDeliveryAddresses(): Promise<DeliveryAddress[]> {
  return api.get<DeliveryAddress[]>("/orders/delivery-addresses");
}

export function createDeliveryAddress(payload: Omit<DeliveryAddress, "id" | "usage_count">): Promise<DeliveryAddress> {
  return api.post<DeliveryAddress>("/orders/delivery-addresses", payload);
}

export function updateDeliveryAddress(
  id: number,
  payload: Omit<DeliveryAddress, "id" | "usage_count">,
): Promise<DeliveryAddress> {
  return api.put<DeliveryAddress>(`/orders/delivery-addresses/${id}`, payload);
}

export function deleteDeliveryAddress(id: number): Promise<void> {
  return api.del(`/orders/delivery-addresses/${id}`);
}

export function placesHealth(): Promise<{ available: boolean }> {
  return api.get("/orders/places/health");
}

export async function placesAutocomplete(q: string): Promise<Array<{ place_id: string; text?: string; primary?: string; secondary?: string }>> {
  const data = await api.get<unknown>(`/orders/places/autocomplete?q=${encodeURIComponent(q)}`);
  if (!Array.isArray(data)) {
    throw new ApiError(502, "Adresssuche hat eine ungültige Antwort geliefert.");
  }
  return data;
}

export function placesDetails(placeId: string): Promise<{
  strasse?: string;
  hausnummer?: string;
  postleitzahl?: string;
  stadt?: string;
  place_id?: string;
}> {
  return api.get(`/orders/places/details?place_id=${encodeURIComponent(placeId)}`);
}

/** @deprecated Bitte `loadOrderSupplierOptions` aus orderSupplierAutocomplete nutzen. */
export function searchHeroSuppliers(q: string): Promise<HeroSupplier[]> {
  const usp = new URLSearchParams();
  const term = (q || "").trim();
  if (term) usp.set("q", term);
  usp.set("limit", term ? "40" : "25");
  return api.get<HeroSupplier[]>(`/orders/hero/suppliers?${usp}`);
}

export function searchHeroSupplierContacts(
  supplierId: string,
  q: string,
): Promise<HeroSupplierContact[]> {
  const usp = new URLSearchParams();
  if (supplierId) usp.set("supplier_id", supplierId);
  if (q.trim()) usp.set("q", q.trim());
  return api.get<HeroSupplierContact[]>(`/orders/hero/supplier-contacts?${usp}`);
}

export function topOrderProducts(supplierId?: string | number | null): Promise<TopProductRow[]> {
  const sup =
    supplierId != null && !String(supplierId).startsWith("name:")
      ? `&supplier_id=${encodeURIComponent(String(supplierId))}`
      : "";
  return api.get<TopProductRow[]>(`/orders/products/top-by-orders?limit=15${sup}`);
}

export interface ComposeOrderMailResult {
  subject: string;
  body: string;
  bestellnummern?: Record<string, string>;
  lauf?: Record<string, number>;
}

export function composeOrderMail(draft: OrderDraftIn): Promise<ComposeOrderMailResult> {
  return api.post<ComposeOrderMailResult>("/orders/compose", { draft });
}

export function finalizeOrder(draft: OrderDraftIn, draftId?: number | null): Promise<FinalizeResult> {
  return api.post<FinalizeResult>("/orders/finalize", {
    draft,
    draft_id: draftId ?? undefined,
  });
}

export function updateOrder(orderId: number, draft: OrderDraftIn, reason?: string): Promise<FinalizeResult> {
  return api.put<FinalizeResult>(`/orders/${orderId}`, { draft, reason });
}

export function patchOrderMailContent(
  orderIds: number[],
  subject: string,
  bodyPlain: string,
  opts?: { bodyHtml?: string; submissionChannel?: "mailto" | "graph" | "copy" },
): Promise<void> {
  return api.patch("/orders/mail-content", {
    order_ids: orderIds,
    subject,
    body: bodyPlain,
    body_html: opts?.bodyHtml,
    submission_channel: opts?.submissionChannel,
  });
}

export function cancelFreshOrders(orderIds: number[]): Promise<void> {
  return api.post("/orders/cancel-create", { order_ids: orderIds });
}

export function cancelOrder(orderId: number): Promise<{ ok: boolean; cancelled_at?: string }> {
  return api.post(`/orders/${orderId}/cancel`, {});
}

export function restoreOrder(orderId: number): Promise<{ ok: boolean }> {
  return api.post(`/orders/${orderId}/restore`, {});
}

export function markOrderDelivered(
  orderId: number,
): Promise<{ ok: boolean; geliefert_at?: string; already_delivered?: boolean }> {
  return api.post(`/orders/${orderId}/mark-delivered`, {});
}

export function unmarkOrderDelivered(
  orderId: number,
): Promise<{ ok: boolean; already_not_delivered?: boolean }> {
  return api.post(`/orders/${orderId}/unmark-delivered`, {});
}

export function sendOrderMail(payload: {
  order_ids: number[];
  subject: string;
  body: string;
  html_body?: string;
  to?: string | null;
  sharepoint_item_ids?: string[];
  attachments?: {
    filename: string;
    content_type: string;
    content_base64: string;
  }[];
}): Promise<unknown> {
  return api.post("/orders/send-mail", payload);
}

export function mailSharepointSuggestions(opts: {
  orderIds?: number[];
  auftragIds?: number[];
}): Promise<{
  items: SharePointMailItem[];
  sharepoint_projects?: number;
  warnings?: unknown[];
}> {
  return api.post("/orders/mail-sharepoint-suggestions", {
    order_ids: opts.orderIds ?? [],
    auftrag_ids: opts.auftragIds ?? [],
  });
}

export function getOrderChangeLog(orderId: number): Promise<ChangeLogEntry[]> {
  return api.get<ChangeLogEntry[]>(`/orders/${orderId}/change-log`);
}

export interface OrderDeliveryState {
  lieferart: "lieferung" | "abholung";
  lieferadresse_id?: number | null;
  lieferadresse_zhd?: string | null;
  lieferadresse_firma?: string | null;
  lieferadresse_strasse?: string | null;
  lieferadresse_hausnummer?: string | null;
  lieferadresse_postleitzahl?: string | null;
  lieferadresse_stadt?: string | null;
}

export function getOrderDeliveryState(orderId: number): Promise<OrderDeliveryState> {
  return api.get<OrderDeliveryState>(`/orders/${orderId}/delivery-state`);
}

export function peekOrderNumber(year: number): Promise<{ jahr: number; last_lauf: number }> {
  return api.get(`/orders/peek-number?jahr=${year}`);
}

export interface CoverPrintUrlRow {
  order_id: number;
  bestellnummer?: string;
  url: string;
}

type OrderCoverPrintUrlsResponse = {
  urls?: CoverPrintUrlRow[];
  items?: CoverPrintUrlRow[];
  one_per_bestellnummer?: boolean;
};

export function getOrderCoverTemplateSettings(): Promise<{ one_per_bestellnummer: boolean }> {
  return api.get<{ one_per_bestellnummer?: boolean }>("/orders/cover-template").then((data) => ({
    one_per_bestellnummer: !!data?.one_per_bestellnummer,
  }));
}

export async function fetchOrderCoverPrintUrls(
  orderIds: number[],
  onePerBestellnummer?: boolean,
): Promise<{ items: CoverPrintUrlRow[]; one_per_bestellnummer?: boolean }> {
  const body: { order_ids: number[]; one_per_bestellnummer?: boolean } = { order_ids: orderIds };
  if (typeof onePerBestellnummer === "boolean") body.one_per_bestellnummer = onePerBestellnummer;
  const data = await api.post<OrderCoverPrintUrlsResponse>("/orders/cover/pdf/print-urls", body);
  return {
    items: data.items ?? data.urls ?? [],
    one_per_bestellnummer: data.one_per_bestellnummer,
  };
}

export function listOrderWerkszeugnisse(
  orderId: number,
): Promise<{ items: OrderWerkszeugnis[]; count: number }> {
  return api.get(`/orders/${orderId}/werkszeugnisse`);
}

export function uploadOrderWerkszeugnis(
  orderId: number,
  file: File,
  orderLineId?: number | null,
): Promise<{ item: OrderWerkszeugnis; werkszeugnis_count: number; warnings: string[] }> {
  const form = new FormData();
  form.append("file", file);
  if (orderLineId != null && orderLineId > 0) {
    form.append("order_line_id", String(orderLineId));
  }
  return api.postForm(`/orders/${orderId}/werkszeugnisse`, form);
}

export function deleteOrderWerkszeugnis(
  orderId: number,
  fileId: number,
): Promise<{ ok: boolean; werkszeugnis_count: number; warnings: string[] }> {
  return api.del(`/orders/${orderId}/werkszeugnisse/${fileId}`);
}

export interface OrderExcelImportError {
  row: number;
  bestellnummer?: string | null;
  reason: string;
}

export interface OrderExcelImportResult {
  dry_run: boolean;
  include_angefragt: boolean;
  rows: number;
  imported: number;
  skipped_existing: number;
  skipped_status: number;
  failed: number;
  waiting_for_auftrag?: number;
  linked_suppliers: number;
  counter: { jahr: number; last_lauf_at_least: number }[];
  order_ids: number[];
  errors: OrderExcelImportError[];
  errors_truncated: boolean;
}

export function importOrdersExcel(
  file: File,
  opts?: { dryRun?: boolean; includeAngefragt?: boolean },
): Promise<OrderExcelImportResult> {
  const form = new FormData();
  form.append("file", file);
  const params = new URLSearchParams();
  if (opts?.dryRun) params.set("dry_run", "true");
  if (opts?.includeAngefragt) params.set("include_angefragt", "true");
  const query = params.toString();
  return api.postForm(`/orders/excel-import${query ? `?${query}` : ""}`, form);
}

export function updateOrderLineLot(
  orderId: number,
  lineId: number,
  lotNummer: string | null,
): Promise<{ ok: boolean; id: number; lot_nummer?: string | null }> {
  return api.patch(`/orders/${orderId}/lines/${lineId}/lot`, { lot_nummer: lotNummer });
}
